import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';

import { BoltOutlined, ErrorOutline } from '@mui/icons-material';
import InputFieldWithLabel from '../../components/layouts/InputFieldWithLabel';
import PasswordFieldWithLabel from '../../components/layouts/PasswordFieldWithLabel';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

const LoginForm = ({
  loading,
  errorMsg,
  onClick,
  onForgotPassword,
}) => {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [validationMsg, setValidationMsg] = useState("");

  // A real <form>, so Enter submits and password managers recognise it
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email || !password) {
      setValidationMsg("Enter your email and password.");
      return;
    }
    setValidationMsg("");
    onClick({ email, password });
  };

  const message = validationMsg || errorMsg;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: "column",
        width: "100%",
        maxWidth: 400,
        gap: 2.5,
      }}
    >
      {/* Brand, only shown when the welcome panel is hidden on small screens */}
      <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #3B9BFF 0%, #137FEC 55%, #142784 100%)",
        }}>
          <BoltOutlined sx={{ color: "white" }} />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#FFF7ED" }}>POSH Energy</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Typography component="h1" sx={{
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "#FFF7ED",
          lineHeight: 1.2,
        }}>
          Welcome back
        </Typography>
        <Typography sx={{ fontSize: 15, color: "rgba(255, 244, 232, 0.65)", mt: 1 }}>
          Sign in to your POSH Energy account
        </Typography>
      </Box>

      {/* Email */}
      <InputFieldWithLabel
        id="email"
        label="Email address"
        type="email"
        placeholder="you@company.com"
        autoComplete="email"
        autoFocus
        onValueChange={(value) => setEmail(value)}
      />

      {/* Password */}
      <PasswordFieldWithLabel
        id="password"
        label="Password"
        placeholder="Enter your password"
        onValueChange={(value) => setPassword(value)}
        subText="Forgot password?"
        onSubTextClick={onForgotPassword}
      />

      {/* Error message */}
      {message &&
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: "10px",
            backgroundColor: "rgba(239, 68, 68, 0.12)",
            border: "1px solid rgba(248, 113, 113, 0.35)",
            color: "#FCA5A5",
          }}
        >
          <ErrorOutline sx={{ fontSize: 18, mt: "1px" }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>{message}</Typography>
        </Box>
      }

      {/* Button */}
      <Button
        type="submit"
        variant="contained"
        disabled={loading}
        disableElevation
        sx={{
          height: 48,
          mt: 1,
          borderRadius: "10px",
          fontSize: 15,
          fontWeight: 600,
          textTransform: "none",
          backgroundColor: "#137FEC",
          "&:hover": { backgroundColor: "#0F6FD1" },
          "&.Mui-disabled": { backgroundColor: "#137FEC", opacity: 0.7, color: "white" },
        }}
      >
        {loading
          ? <CircularProgress size={22} sx={{ color: 'white' }} />
          : "Sign in"
        }
      </Button>

      <Typography sx={{ fontSize: 14, color: "rgba(255, 244, 232, 0.6)", textAlign: "center", mt: 1 }}>
        Don't have an account yet?{" "}
        <Link href="" underline="hover" sx={{ fontWeight: 600, color: "#4DA3FF" }}>
          Request access
        </Link>
      </Typography>

      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        mt: 3,
        pt: 3,
        borderTop: "1px solid rgba(255, 244, 232, 0.1)",
        color: "rgba(255, 244, 232, 0.45)",
      }}>
        <VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}>
          256-bit encrypted connection
        </Typography>
      </Box>
    </Box>
  )
}

export default LoginForm
